import { Box, TextField, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { makeStyles } from '@mui/styles';
import * as api from '../api/index';
import NumberFormat from 'react-number-format';
import { triggerApplicationSync, pushDL } from '@lendica/api';
import { useApplication } from '../context/useApplication';
import { useAutosave } from '../hooks/useAutosave';
import LoadingButton from '@mui/lab/LoadingButton';

const useStyles = makeStyles(theme => ({
    row: {
        display: 'flex',
        gap: '1rem',
        flexDirection: 'row',

        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
}));

const validationSchema = yup.object({
    first_name: yup.string('Enter your first name').required('First name is required'),
    last_name: yup.string('Enter your last name').required('Last name is required'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .required('Email is required')
        .test('is-work-email', 'Use your company email address to continue.', async value => {
            if (!value) return true;

            try {
                const result = await api.validateWorkEmail(value);
                // console.log('Validation result:', result);

                // If it's a personal email, validation fails
                if (result.isPersonalEmail) {
                    return false;
                }

                return true;
            } catch (error) {
                console.error('Error validating work email:', error);
                return false;
            }
        }),
    phone_number: yup
        .string()
        .test('len', 'Please provide a valid phone number', val => val?.length === 10)
        .required('Phone is required'),
});

export const PersonalInfoForm = ({ application, handleApplicationChange, handleNext }) => {
    const { env } = useApplication();

    const formik = useFormik({
        initialValues: {
            first_name: application.first_name || '',
            last_name: application.last_name || '',
            email: application.email || '',
            phone_number: application.phone_number || '',
        },
        enableReinitialize: true, // Update form when application prop changes
        validationSchema: validationSchema,
        onSubmit: async values => {
            await api.postApplication(values);
            handleApplicationChange(values);

            if (env === 'prod') {
                // fire-and-forget sync, ignore errors
                triggerApplicationSync(application.id)
                    .then(data => {
                        api.postApplication({
                            meta: {
                                ...application.meta,
                                hs: {
                                    ...data,
                                },
                            },
                        });
                    })
                    .catch(error => {
                        console.error('Error syncing application:', error);
                    });
            }

            pushDL('application_step', {
                step: 'application_personal_submit',
                application_id: application.id,
                company_id: application.company,
                email: application.email,
                phone: application.phone_number,
                first_name: application.first_name,
                last_name: application.last_name,
            });

            handleNext();
        },
    });

    const classes = useStyles();

    // Use the autosave hook
    const { isAutosaving } = useAutosave(formik, handleApplicationChange, {
        transformValues: values => {
            const transformed = {};

            // Only include non-empty values for autosave
            Object.keys(values).forEach(key => {
                const value = values[key];

                // Skip empty/null/undefined values
                if (value === '' || value === null || value === undefined) {
                    return;
                }

                transformed[key] = value;
            });

            return transformed;
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} style={{ margin: '1.5rem 0' }}>
            <Box className={classes.row} my={2}>
                <TextField
                    label="First Name"
                    name="first_name"
                    id="first_name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                    helperText={formik.touched.first_name && formik.errors.first_name}
                    size="small"
                    fullWidth
                    sx={{ flex: 1 }}
                    color="primary"
                    inputProps={{ 'data-testid': 'first_name' }}
                />
                <TextField
                    label="Last Name"
                    name="last_name"
                    id="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                    helperText={formik.touched.last_name && formik.errors.last_name}
                    size="small"
                    fullWidth
                    sx={{ flex: 1 }}
                    color="primary"
                    inputProps={{ 'data-testid': 'last_name' }}
                />
            </Box>

            <Box sx={{ display: 'flex' }} mb={2}>
                <TextField
                    label="Work Email"
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={
                        (formik.touched.email && formik.errors.email) ||
                        'Please use your work email (business domain) if available.'
                    }
                    size="small"
                    fullWidth
                    color="primary"
                    inputProps={{ 'data-testid': 'email' }}
                    // disabled={!!application.email}
                />
            </Box>

            <Box sx={{ display: 'flex' }} mb={2}>
                <NumberFormat
                    mask="_"
                    customInput={TextField}
                    format="+1 (###) ###-####"
                    label="Phone number"
                    name="phone_number"
                    id="phone_number"
                    value={formik.values.phone_number}
                    onValueChange={({ value }) => {
                        formik.setFieldValue('phone_number', value);
                    }}
                    isNumericString
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                    helperText={formik.touched.phone_number && formik.errors.phone_number}
                    size="small"
                    fullWidth
                    sx={{ flex: 1 }}
                    color="primary"
                    disabled={true}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                    sx={{
                        flex: 1,
                        mt: 1,
                        mr: 1,
                        borderColor: 'primary.light',
                        border: '1px solid',
                        color: 'primary.main',
                    }}
                    disabled
                >
                    Back
                </Button>
                <LoadingButton
                    sx={{ flex: 1, mt: 1 }}
                    variant="contained"
                    color="primary"
                    type="submit"
                    data-testid="personal-info-form-submit-button"
                    disabled={formik.isSubmitting || formik.isValidating || !formik.isValid}
                    loading={formik.isSubmitting}
                >
                    Continue
                </LoadingButton>
            </Box>
        </form>
    );
};
